import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { accountAPI } from '../services/api';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const API_BASE = process.env.REACT_APP_API_URL || 'http://95.179.207.212:8080';
const WS_BASE = API_BASE.replace('/api', '');


// 숫자 변경 시 글자색만 깜박이는 컴포넌트
const FlashValue: React.FC<{ value: number | null | undefined; format: (v: number) => string; color?: string }> = ({ value, format, color }) => {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prevRef.current !== undefined && prevRef.current !== value) {
      setFlash(true);
      setTimeout(() => setFlash(false), 600);
    }
    prevRef.current = value ?? undefined;
  }, [value]);

  if (value == null) return <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>;

  return (
    <span style={{
      color: flash ? '#ffe066' : (color || 'rgba(255,255,255,0.8)'),
      fontWeight: 600,
      transition: 'color 0.1s',
      fontFamily: 'monospace',
    }}>
      {format(value)}
    </span>
  );
};

const ABookPage: React.FC = () => {
  const [aBookStates, setABookStates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [returning, setReturning] = useState<string | null>(null);
  const [equityMap, setEquityMap] = useState<Record<string, any>>({});
  const [nameMap, setNameMap] = useState<Record<string, string>>({});
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const ruleId = searchParams.get('ruleId');

  const fetchData = async () => {
    try {
      const res = await accountAPI.getABook();
      setABookStates(res.data);
      // API에서 받은 name으로 nameMap 초기화
      const names: Record<string, string> = {};
      res.data.forEach((s: any) => {
        if (s.name) names[s.accountId] = s.name;
      });
      setNameMap(prev => ({ ...names, ...prev }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  // WebSocket 실시간 연결
  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE}/api/ws`),
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe('/topic/equity-updates', (msg) => {
          const data = JSON.parse(msg.body);
          setEquityMap(prev => ({
            ...prev,
            [data.accountId]: {
              equity: data.equity,
              balance: data.balance,
              drawdown: data.drawdown,
            }
          }));
          if (data.name) {
            setNameMap(prev => ({ ...prev, [data.accountId]: data.name }));
          }
        });
      },
    });
    client.activate();
    return () => { client.deactivate(); };
  }, []);

  const handleReturn = async (accountId: string) => {
    if (!window.confirm(`계좌 ${accountId}를 B북으로 복귀시키겠습니까?`)) return;
    setReturning(accountId);
    try {
      await accountAPI.returnToBBook(accountId);
      await fetchData();
    } catch {
      alert('복귀 처리 중 오류가 발생했습니다.');
    } finally {
      setReturning(null);
    }
  };

  const filtered = ruleId
    ? aBookStates.filter((s: any) => String(s.ruleId) === ruleId)
    : aBookStates;

  const ddColor = (v: number) => {
    if (v == null) return 'rgba(255,255,255,0.5)';
    if (v <= -10) return '#ff4757';
    if (v <= -5) return '#ff6b6b';
    if (v < 0) return '#f7971e';
    return '#2ed573';
  };

  // 실시간 drawdown 기준 요약
  const drawdowns = filtered.map((s: any) => equityMap[s.accountId]?.drawdown ?? s.triggerDrawdown ?? 0);
  const avgDrawdown = drawdowns.length > 0 ? drawdowns.reduce((a: number, b: number) => a + b, 0) / drawdowns.length : 0;
  const maxDrawdown = drawdowns.length > 0 ? Math.min(...drawdowns) : 0;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🔴 A북 현황</h1>
          <p style={styles.subtitle}>
            {ruleId ? '룰 필터 적용 중' : '전체 A북 계좌'} · 10초마다 자동 갱신
          </p>
        </div>
        <button style={styles.refreshBtn} onClick={fetchData}>🔄 새로고침</button>
      </div>

      {/* 요약 */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>현재 A북 계좌</div>
          <div style={{ ...styles.summaryValue, color: '#ff6b6b' }}>{filtered.length}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>평균 Drawdown (실시간)</div>
          <div style={{ ...styles.summaryValue, color: '#f7971e' }}>{avgDrawdown.toFixed(2)}%</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>최대 Drawdown (실시간)</div>
          <div style={{ ...styles.summaryValue, color: '#ff6b6b' }}>{maxDrawdown.toFixed(2)}%</div>
        </div>
      </div>

      {/* 테이블 */}
      {loading ? (
        <div style={styles.loading}>로딩 중...</div>
      ) : filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>✅</div>
          <div style={styles.emptyText}>현재 A북 전환된 계좌가 없습니다.</div>
          <div style={styles.emptySub}>모든 계좌가 B북으로 정상 운영 중입니다.</div>
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>계좌번호</th>
                <th style={styles.th}>이름</th>
                <th style={styles.th}>룰명</th>
                <th style={styles.th}>Balance</th>
                <th style={styles.th}>Equity</th>
                <th style={styles.th}>현재 Drawdown</th>
                <th style={styles.th}>전환 Drawdown</th>
                <th style={styles.th}>전환 시각</th>
                <th style={styles.th}>상태</th>
                <th style={styles.th}>액션</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((state: any, idx: number) => {
                const rt = equityMap[state.accountId];
                const currentDD = rt?.drawdown ?? null;
                const triggerDD = state.triggerDrawdown;
                return (
                  <tr key={state.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>
                      <span style={styles.accountId}>{state.accountId}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.name}>{nameMap[state.accountId] || '-'}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.ruleName}>{state.ruleName || '-'}</span>
                    </td>
                    <td style={styles.td}>
                      <FlashValue value={rt?.balance} format={v => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    </td>
                    <td style={styles.td}>
                      <FlashValue value={rt?.equity} format={v => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    </td>
                    <td style={styles.td}>
                      <FlashValue value={currentDD} format={v => `${v.toFixed(2)}%`} color={ddColor(currentDD)} />
                    </td>
                    <td style={styles.td}>
                      <span style={{ color: '#ff6b6b', fontWeight: 600 }}>
                        {triggerDD != null ? `${Number(triggerDD).toFixed(2)}%` : '-'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.datetime}>
                        {state.triggeredAt ? new Date(state.triggeredAt).toLocaleString('ko-KR') : '-'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.aBookBadge}>A북</span>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={returning === state.accountId
                          ? { ...styles.returnBtn, opacity: 0.6 }
                          : styles.returnBtn}
                        onClick={() => handleReturn(state.accountId)}
                        disabled={returning === state.accountId}
                      >
                        {returning === state.accountId ? '처리중...' : '🔄 B북 복귀'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { color: '#fff', width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 700, color: '#fff', margin: '0 0 4px' },
  subtitle: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  refreshBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '28px' },
  summaryCard: { background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: '16px', padding: '20px', textAlign: 'center' },
  summaryLabel: { color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' },
  summaryValue: { fontSize: '32px', fontWeight: 700 },
  tableWrap: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'auto', width: '100%' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  thead: { background: 'rgba(255,255,255,0.05)' },
  th: { padding: '14px 16px', textAlign: 'left' as const, fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.05em', textTransform: 'uppercase' as const, borderBottom: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap' as const, background: 'rgba(255,255,255,0.03)' },
  trEven: { background: 'transparent' },
  trOdd: { background: 'rgba(255,255,255,0.02)' },
  td: { padding: '13px 16px', fontSize: '13px', borderBottom: '1px solid rgba(255,255,255,0.05)', verticalAlign: 'middle' as const },
  accountId: { fontFamily: 'monospace', fontSize: '14px', fontWeight: 700, color: '#667eea' },
  name: { color: 'rgba(255,255,255,0.8)', fontSize: '13px' },
  ruleName: { color: 'rgba(255,255,255,0.7)', fontSize: '12px', background: 'rgba(102,126,234,0.15)', padding: '2px 8px', borderRadius: '6px' },
  money: { fontFamily: 'monospace', fontSize: '13px', color: 'rgba(255,255,255,0.8)' },
  datetime: { color: 'rgba(255,255,255,0.5)', fontSize: '12px' },
  aBookBadge: { background: 'rgba(255,107,107,0.2)', color: '#ff6b6b', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 },
  returnBtn: { background: 'rgba(0,210,255,0.15)', border: '1px solid rgba(0,210,255,0.3)', borderRadius: '8px', padding: '6px 14px', color: '#00d2ff', fontSize: '12px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' as const },
  emptyState: { textAlign: 'center', padding: '80px 40px' },
  emptyIcon: { fontSize: '64px', marginBottom: '16px' },
  emptyText: { color: '#fff', fontSize: '18px', fontWeight: 600, marginBottom: '8px' },
  emptySub: { color: 'rgba(255,255,255,0.4)', fontSize: '14px' },
  loading: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
};

export default ABookPage;
